import * as XLSX from 'xlsx';

// Função para formatar data no padrão brasileiro
const formatarData = (dataString) => {
  if (!dataString) return '';
  // Criar data com horário fixo no meio do dia para evitar problemas de fuso horário
  const [ano, mes, dia] = dataString.split('-');
  const data = new Date(ano, mes - 1, dia, 12, 0, 0);
  return data.toLocaleDateString('pt-BR');
};

// Função para formatar valor monetário
const formatarValor = (valor) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor || 0);
};

// Função para criar estilo de célula
const criarEstiloCelula = (opcoes = {}) => {
  const {
    negrito = false,
    italico = false,
    corFundo = null,
    corTexto = null,
    alinhamento = 'left',
    borda = false,
    tamanhoFonte = 11,
    tipoBorda = 'thin',
    corBorda = '000000'
  } = opcoes;

  const estilo = {
    font: {
      name: 'Arial',
      sz: tamanhoFonte,
      bold: negrito,
      italic: italico,
      color: corTexto ? { rgb: corTexto } : undefined
    },
    alignment: {
      horizontal: alinhamento,
      vertical: 'center',
      wrapText: true
    },
    fill: corFundo ? {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { rgb: corFundo }
    } : undefined,
    border: borda ? {
      top: { style: tipoBorda, color: { rgb: corBorda } },
      bottom: { style: tipoBorda, color: { rgb: corBorda } },
      left: { style: tipoBorda, color: { rgb: corBorda } },
      right: { style: tipoBorda, color: { rgb: corBorda } }
    } : undefined
  };

  return estilo;
};

// Função para aplicar estilos a um intervalo de células
const aplicarEstilos = (ws, range, estilo) => {
  const [inicio, fim] = range.split(':');
  const colInicio = inicio.match(/[A-Z]+/)[0];
  const linhaInicio = parseInt(inicio.match(/\d+/)[0]);
  const colFim = fim.match(/[A-Z]+/)[0];
  const linhaFim = parseInt(fim.match(/\d+/)[0]);

  for (let linha = linhaInicio; linha <= linhaFim; linha++) {
    for (let col = colInicio.charCodeAt(0); col <= colFim.charCodeAt(0); col++) {
      const celula = `${String.fromCharCode(col)}${linha}`;
      if (!ws[celula]) continue;
      ws[celula].s = estilo;
    }
  }
};

// Função para gerar o relatório Excel
export const gerarRelatorioExcel = (dados) => {
  const {
    transacoes,
    saldoAtual,
    saldoFiltrado,
    resumoPorEntidade,
    filtros
  } = dados;

  // Criar uma nova pasta de trabalho
  const wb = XLSX.utils.book_new();
  
  // 1. Planilha de Resumo Geral
  const resumoGeral = [
    ['RELATÓRIO FINANCEIRO'],
    [''],
    ['Período do Relatório:'],
    [`De: ${formatarData(filtros.dataInicio)}`],
    [`Até: ${formatarData(filtros.dataFim)}`],
    [''],
    ['Resumo Geral'],
    ['Saldo Total', formatarValor(saldoAtual)],
    ['Saldo do Período', formatarValor(saldoFiltrado)],
    [''],
    ['Resumo por Entidade'],
    ['Entidade', 'Tipo', 'Entradas', 'Saídas', 'Saldo']
  ];

  // Adicionar dados do resumo por entidade
  Object.entries(resumoPorEntidade).forEach(([entidade, dados]) => {
    resumoGeral.push([
      dados.nome,
      dados.tipo,
      formatarValor(dados.entradas),
      formatarValor(dados.saidas),
      formatarValor(dados.saldo)
    ]);
  });

  // 2. Planilha de Transações
  const transacoesData = [
    ['RELATÓRIO DE TRANSAÇÕES'],
    [''],
    ['Período do Relatório:'],
    [`De: ${formatarData(filtros.dataInicio)}`],
    [`Até: ${formatarData(filtros.dataFim)}`],
    [''],
    ['Data', 'Tipo', 'Descrição', 'Categoria', 'Entidade', 'Forma de Pagamento', 'Valor', 'ID']
  ];

  // Adicionar transações
  transacoes.forEach(transacao => {
    transacoesData.push([
      formatarData(transacao.data),
      transacao.tipo === 'entrada' ? 'Entrada' : 'Saída',
      transacao.descricao,
      transacao.categoria,
      transacao.entidadeNome,
      transacao.formaPagamento,
      formatarValor(transacao.valor),
      transacao.id
    ]);
  });

  // Criar as planilhas
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoGeral);
  const wsTransacoes = XLSX.utils.aoa_to_sheet(transacoesData);

  // Aplicar estilos ao Resumo Geral
  // Título principal
  aplicarEstilos(wsResumo, 'A1:A1', criarEstiloCelula({
    negrito: true,
    tamanhoFonte: 18,
    corFundo: '1F4E78',
    corTexto: 'FFFFFF',
    alinhamento: 'center',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Período
  aplicarEstilos(wsResumo, 'A3:A5', criarEstiloCelula({
    negrito: true,
    corFundo: 'D9E1F2',
    borda: true,
    tipoBorda: 'thin',
    corBorda: '4472C4'
  }));

  // Subtítulo Resumo Geral
  aplicarEstilos(wsResumo, 'A7:A7', criarEstiloCelula({
    negrito: true,
    tamanhoFonte: 14,
    corFundo: '4472C4',
    corTexto: 'FFFFFF',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Valores do Resumo Geral
  aplicarEstilos(wsResumo, 'A8:B9', criarEstiloCelula({
    borda: true,
    alinhamento: 'left',
    corFundo: 'F2F2F2',
    tipoBorda: 'thin',
    corBorda: '4472C4'
  }));

  // Cabeçalho Resumo por Entidade
  aplicarEstilos(wsResumo, 'A11:E11', criarEstiloCelula({
    negrito: true,
    corFundo: '4472C4',
    corTexto: 'FFFFFF',
    alinhamento: 'center',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Dados do Resumo por Entidade
  const linhaInicioEntidades = 12;
  const linhaFimEntidades = linhaInicioEntidades + Object.keys(resumoPorEntidade).length - 1;

  // Aplicar estilo zebrado nas linhas
  for (let linha = linhaInicioEntidades; linha <= linhaFimEntidades; linha++) {
    const corFundo = linha % 2 === 0 ? 'FFFFFF' : 'F2F2F2';
    aplicarEstilos(wsResumo, `A${linha}:E${linha}`, criarEstiloCelula({
      borda: true,
      alinhamento: 'left',
      corFundo: corFundo,
      tipoBorda: 'thin',
      corBorda: '4472C4'
    }));

    // Coluna de Saldo (E) com formatação condicional
    const celulaSaldo = `E${linha}`;
    if (wsResumo[celulaSaldo]) {
      const valor = parseFloat(wsResumo[celulaSaldo].v.replace(/[^\d,-]/g, '').replace(',', '.'));
      wsResumo[celulaSaldo].s = criarEstiloCelula({
        corTexto: valor >= 0 ? '008000' : 'FF0000',
        negrito: true,
        alinhamento: 'right',
        corFundo: valor >= 0 ? 'E2EFDA' : 'FFEBE9',
        borda: true,
        tipoBorda: 'thin',
        corBorda: '4472C4'
      });
    }
  }

  // Aplicar estilos à Planilha de Transações
  // Título principal
  aplicarEstilos(wsTransacoes, 'A1:A1', criarEstiloCelula({
    negrito: true,
    tamanhoFonte: 18,
    corFundo: '1F4E78',
    corTexto: 'FFFFFF',
    alinhamento: 'center',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Período
  aplicarEstilos(wsTransacoes, 'A3:A5', criarEstiloCelula({
    negrito: true,
    corFundo: 'D9E1F2',
    borda: true,
    tipoBorda: 'thin',
    corBorda: '4472C4'
  }));

  // Cabeçalho das colunas
  aplicarEstilos(wsTransacoes, 'A7:H7', criarEstiloCelula({
    negrito: true,
    corFundo: '4472C4',
    corTexto: 'FFFFFF',
    alinhamento: 'center',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Dados das transações com estilo zebrado
  const linhaInicioTransacoes = 8;
  const linhaFimTransacoes = linhaInicioTransacoes + transacoes.length - 1;

  for (let linha = linhaInicioTransacoes; linha <= linhaFimTransacoes; linha++) {
    const corFundo = linha % 2 === 0 ? 'FFFFFF' : 'F2F2F2';
    aplicarEstilos(wsTransacoes, `A${linha}:H${linha}`, criarEstiloCelula({
      borda: true,
      corFundo: corFundo,
      tipoBorda: 'thin',
      corBorda: '4472C4'
    }));

    // Coluna de Tipo (B)
    const celulaTipo = `B${linha}`;
    if (wsTransacoes[celulaTipo]) {
      const isEntrada = wsTransacoes[celulaTipo].v === 'Entrada';
      wsTransacoes[celulaTipo].s = criarEstiloCelula({
        corTexto: isEntrada ? '008000' : 'FF0000',
        negrito: true,
        alinhamento: 'center',
        corFundo: isEntrada ? 'E2EFDA' : 'FFEBE9',
        borda: true,
        tipoBorda: 'thin',
        corBorda: '4472C4'
      });
    }

    // Coluna de Valor (G)
    const celulaValor = `G${linha}`;
    if (wsTransacoes[celulaValor]) {
      const valor = parseFloat(wsTransacoes[celulaValor].v.replace(/[^\d,-]/g, '').replace(',', '.'));
      wsTransacoes[celulaValor].s = criarEstiloCelula({
        corTexto: valor >= 0 ? '008000' : 'FF0000',
        negrito: true,
        alinhamento: 'right',
        corFundo: valor >= 0 ? 'E2EFDA' : 'FFEBE9',
        borda: true,
        tipoBorda: 'thin',
        corBorda: '4472C4'
      });
    }
  }

  // Ajustar largura das colunas
  const colWidths = [
    { wch: 15 }, // Data
    { wch: 10 }, // Tipo
    { wch: 40 }, // Descrição
    { wch: 20 }, // Categoria
    { wch: 25 }, // Entidade
    { wch: 15 }, // Forma de Pagamento
    { wch: 15 }, // Valor
    { wch: 15 }  // ID
  ];

  wsTransacoes['!cols'] = colWidths;
  wsResumo['!cols'] = colWidths;

  // Adicionar as planilhas ao workbook
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo Geral');
  XLSX.utils.book_append_sheet(wb, wsTransacoes, 'Transações');

  // Gerar o arquivo Excel
  const nomeArquivo = `relatorio_financeiro_${formatarData(filtros.dataInicio)}_${formatarData(filtros.dataFim)}.xlsx`;
  XLSX.writeFile(wb, nomeArquivo);

  return nomeArquivo;
};

// Função para gerar relatório específico por entidade
export const gerarRelatorioEntidade = (dados, entidade) => {
  const {
    transacoes,
    filtros
  } = dados;

  // Filtrar transações da entidade
  const transacoesEntidade = transacoes.filter(t => t.entidadeNome === entidade);

  // Calcular totais
  const totais = transacoesEntidade.reduce((acc, t) => {
    const valor = parseFloat(t.valor) || 0;
    if (t.tipo === 'entrada') {
      acc.entradas += valor;
    } else {
      acc.saidas += valor;
    }
    return acc;
  }, { entradas: 0, saidas: 0 });

  // Criar workbook
  const wb = XLSX.utils.book_new();

  // Dados do relatório
  const dadosRelatorio = [
    [`RELATÓRIO FINANCEIRO - ${entidade}`],
    [''],
    ['Período do Relatório:'],
    [`De: ${formatarData(filtros.dataInicio)}`],
    [`Até: ${formatarData(filtros.dataFim)}`],
    [''],
    ['Resumo'],
    ['Total de Entradas', formatarValor(totais.entradas)],
    ['Total de Saídas', formatarValor(totais.saidas)],
    ['Saldo', formatarValor(totais.entradas - totais.saidas)],
    [''],
    ['Transações'],
    ['Data', 'Tipo', 'Descrição', 'Categoria', 'Forma de Pagamento', 'Valor', 'ID']
  ];

  // Adicionar transações
  transacoesEntidade.forEach(transacao => {
    dadosRelatorio.push([
      formatarData(transacao.data),
      transacao.tipo === 'entrada' ? 'Entrada' : 'Saída',
      transacao.descricao,
      transacao.categoria,
      transacao.formaPagamento,
      formatarValor(transacao.valor),
      transacao.id
    ]);
  });

  // Criar planilha
  const ws = XLSX.utils.aoa_to_sheet(dadosRelatorio);

  // Aplicar estilos
  // Título principal
  aplicarEstilos(ws, 'A1:A1', criarEstiloCelula({
    negrito: true,
    tamanhoFonte: 18,
    corFundo: '1F4E78',
    corTexto: 'FFFFFF',
    alinhamento: 'center',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Período
  aplicarEstilos(ws, 'A3:A5', criarEstiloCelula({
    negrito: true,
    corFundo: 'D9E1F2',
    borda: true,
    tipoBorda: 'thin',
    corBorda: '4472C4'
  }));

  // Subtítulo Resumo
  aplicarEstilos(ws, 'A7:A7', criarEstiloCelula({
    negrito: true,
    tamanhoFonte: 14,
    corFundo: '4472C4',
    corTexto: 'FFFFFF',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Valores do Resumo
  aplicarEstilos(ws, 'A8:B10', criarEstiloCelula({
    borda: true,
    alinhamento: 'left',
    corFundo: 'F2F2F2',
    tipoBorda: 'thin',
    corBorda: '4472C4'
  }));

  // Formatação condicional para saldo
  const celulaSaldo = 'B10';
  if (ws[celulaSaldo]) {
    const valor = parseFloat(ws[celulaSaldo].v.replace(/[^\d,-]/g, '').replace(',', '.'));
    ws[celulaSaldo].s = criarEstiloCelula({
      corTexto: valor >= 0 ? '008000' : 'FF0000',
      negrito: true,
      alinhamento: 'right',
      corFundo: valor >= 0 ? 'E2EFDA' : 'FFEBE9',
      borda: true,
      tipoBorda: 'thin',
      corBorda: '4472C4'
    });
  }

  // Cabeçalho das transações
  aplicarEstilos(ws, 'A12:G12', criarEstiloCelula({
    negrito: true,
    corFundo: '4472C4',
    corTexto: 'FFFFFF',
    alinhamento: 'center',
    borda: true,
    tipoBorda: 'medium'
  }));

  // Dados das transações com estilo zebrado
  const linhaInicioTransacoesEntidade = 13;
  const linhaFimTransacoesEntidade = linhaInicioTransacoesEntidade + transacoesEntidade.length - 1;

  for (let linha = linhaInicioTransacoesEntidade; linha <= linhaFimTransacoesEntidade; linha++) {
    const corFundo = linha % 2 === 0 ? 'FFFFFF' : 'F2F2F2';
    aplicarEstilos(ws, `A${linha}:G${linha}`, criarEstiloCelula({
      borda: true,
      corFundo: corFundo,
      tipoBorda: 'thin',
      corBorda: '4472C4'
    }));

    // Coluna de Tipo (B)
    const celulaTipo = `B${linha}`;
    if (ws[celulaTipo]) {
      const isEntrada = ws[celulaTipo].v === 'Entrada';
      ws[celulaTipo].s = criarEstiloCelula({
        corTexto: isEntrada ? '008000' : 'FF0000',
        negrito: true,
        alinhamento: 'center',
        corFundo: isEntrada ? 'E2EFDA' : 'FFEBE9',
        borda: true,
        tipoBorda: 'thin',
        corBorda: '4472C4'
      });
    }

    // Coluna de Valor (F)
    const celulaValor = `F${linha}`;
    if (ws[celulaValor]) {
      const valor = parseFloat(ws[celulaValor].v.replace(/[^\d,-]/g, '').replace(',', '.'));
      ws[celulaValor].s = criarEstiloCelula({
        corTexto: valor >= 0 ? '008000' : 'FF0000',
        negrito: true,
        alinhamento: 'right',
        corFundo: valor >= 0 ? 'E2EFDA' : 'FFEBE9',
        borda: true,
        tipoBorda: 'thin',
        corBorda: '4472C4'
      });
    }
  }

  // Ajustar largura das colunas
  ws['!cols'] = [
    { wch: 15 }, // Data
    { wch: 10 }, // Tipo
    { wch: 40 }, // Descrição
    { wch: 20 }, // Categoria
    { wch: 15 }, // Forma de Pagamento
    { wch: 15 }, // Valor
    { wch: 15 }  // ID
  ];

  // Adicionar planilha ao workbook
  XLSX.utils.book_append_sheet(wb, ws, entidade);

  // Gerar arquivo
  const nomeArquivo = `relatorio_${entidade}_${formatarData(filtros.dataInicio)}_${formatarData(filtros.dataFim)}.xlsx`;
  XLSX.writeFile(wb, nomeArquivo);

  return nomeArquivo;
}; 